import type { RequestOptions } from '../client.js';
import type { ChainSlug } from '../types/chains.js';
import type { HistoricalQueryOptions } from '../types/client.js';
import {
  type SwapQuoteData,
  type GetJupiterQuoteParams,
  type GetJupiterPriceParams,
  type JupiterQuoteData,
  type JupiterPriceData,
  type CurvePoolsData,
  type CurveLlamaLendMarketsData,
  type ApprovalsData,
  type FundingRatesData,
  type HyperliquidFundingData,
  type DydxFundingData,
  type AvantisVaultPriceData,
  chainParamsSchema,
  chainAddressParamsSchema,
  getUniswapV2QuoteParamsSchema,
  getUniswapV3QuoteParamsSchema,
  getUniswapV4QuoteParamsSchema,
  getJupiterQuoteParamsSchema,
  getJupiterPriceParamsSchema,
  getAvantisPriceParamsSchema,
  getPendleV2InfoParamsSchema,
  getApprovalsRevokeParamsSchema,
  getFundingRatesParamsSchema,
  getHyperliquidFundingParamsSchema,
  getDydxFundingParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type {
  UniswapQuoteParams,
  UniswapV4QuoteParams,
  ProtocolPositionResult,
  PendleInfoResult,
  DefiSummaryResult,
  RevokeTxResult,
  DefiProtocolSlug,
  AvantisVaultParams,
} from '../types/index.js';
import { DEFI_POSITION_METHOD_BY_SLUG } from '@spectrum-nodes/api-spec/constants';
import { ValidationError } from '../types/errors.js';
import { parseParams } from '../internals/validation.js';
import { splitHistoricalArgs } from '../internals/utils.js';
import { ChainNamespace } from './base.js';

/** DeFi protocol interactions — swaps, positions, approvals, funding rates */
export class DefiNamespace extends ChainNamespace {
  private getPositionMethod(protocol: DefiProtocolSlug): string {
    const method = (DEFI_POSITION_METHOD_BY_SLUG as Record<string, string>)[protocol];
    if (!method) {
      throw new ValidationError('protocol', `Unsupported protocol: ${protocol}`);
    }
    return method;
  }

  // --- Swap quotes ---

  /** Get a Uniswap V3 swap quote. */
  async getUniswapV3Quote(
    chain: ChainSlug | undefined,
    params: UniswapQuoteParams,
    options?: RequestOptions,
  ): Promise<SwapQuoteData> {
    const validated = parseParams(getUniswapV3QuoteParamsSchema, {
      chain: this.resolve(chain),
      tokenIn: params.tokenIn,
      tokenOut: params.tokenOut,
      amount: params.amount,
      fee: params.fee,
      blockHeight: params.blockHeight,
    });
    return this.client.jsonRpc<SwapQuoteData>('getUniswapV3Quote', validated, options);
  }

  /** Get a Uniswap V2 swap quote. */
  async getUniswapV2Quote(
    chain: ChainSlug | undefined,
    params: Omit<UniswapQuoteParams, 'fee'>,
    options?: RequestOptions,
  ): Promise<SwapQuoteData> {
    const validated = parseParams(getUniswapV2QuoteParamsSchema, {
      chain: this.resolve(chain),
      tokenIn: params.tokenIn,
      tokenOut: params.tokenOut,
      amount: params.amount,
      blockHeight: params.blockHeight,
    });
    return this.client.jsonRpc<SwapQuoteData>('getUniswapV2Quote', validated, options);
  }

  /** Get a Uniswap V4 swap quote. */
  async getUniswapV4Quote(
    chain: ChainSlug | undefined,
    params: UniswapV4QuoteParams,
    options?: RequestOptions,
  ): Promise<SwapQuoteData> {
    const validated = parseParams(getUniswapV4QuoteParamsSchema, {
      chain: this.resolve(chain),
      tokenIn: params.tokenIn,
      tokenOut: params.tokenOut,
      amount: params.amount,
      fee: params.fee,
      tickSpacing: params.tickSpacing,
      blockHeight: params.blockHeight,
    });
    return this.client.jsonRpc<SwapQuoteData>('getUniswapV4Quote', validated, options);
  }

  /** Get a Jupiter swap quote (Solana only). */
  async getJupiterQuote(
    params: GetJupiterQuoteParams,
    options?: RequestOptions,
  ): Promise<JupiterQuoteData> {
    const validated = parseParams(getJupiterQuoteParamsSchema, {
      inputMint: params.inputMint,
      outputMint: params.outputMint,
      amount: params.amount,
      slippageBps: params.slippageBps,
    });
    return this.client.jsonRpc<JupiterQuoteData>('getJupiterQuote', validated, options);
  }

  /** Get Jupiter price (Solana only). */
  async getJupiterPrice(
    params: GetJupiterPriceParams,
    options?: RequestOptions,
  ): Promise<JupiterPriceData> {
    const validated = parseParams(getJupiterPriceParamsSchema, {
      inputMint: params.inputMint,
      outputMint: params.outputMint,
    });
    return this.client.jsonRpc<JupiterPriceData>('getJupiterPrice', validated, options);
  }

  // --- Protocol positions ---

  /** Get a user's position in a specific DeFi protocol. */
  async getPosition(
    chain: ChainSlug | undefined,
    protocol: DefiProtocolSlug,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<ProtocolPositionResult> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const method = this.getPositionMethod(protocol);
    const validated = parseParams(chainAddressParamsSchema, {
      chain: this.resolve(chain),
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<ProtocolPositionResult>(method, validated, requestOptions);
  }

  // --- Curve ---

  /** Get Curve pools for a chain. */
  async getCurvePools(chain?: ChainSlug, options?: RequestOptions): Promise<CurvePoolsData> {
    const validated = parseParams(chainParamsSchema, { chain: this.resolve(chain) });
    return this.client.jsonRpc<CurvePoolsData>('getCurvePools', validated, options);
  }

  /** Get Curve LlamaLend markets. */
  async getCurveLlamaLendMarkets(
    chain?: ChainSlug,
    params?: HistoricalQueryOptions,
    options?: RequestOptions,
  ): Promise<CurveLlamaLendMarketsData> {
    const validated = parseParams(chainParamsSchema, {
      chain: this.resolve(chain),
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<CurveLlamaLendMarketsData>(
      'getCurveLlamaLendMarkets',
      validated,
      options,
    );
  }

  /** Get Curve LlamaLend positions for an address. */
  async getCurveLlamaLendPosition(
    chain: ChainSlug | undefined,
    address: string,
    params?: HistoricalQueryOptions,
    options?: RequestOptions,
  ): Promise<ProtocolPositionResult> {
    const validated = parseParams(chainAddressParamsSchema, {
      chain: this.resolve(chain),
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<ProtocolPositionResult>('getCurveLlamaLend', validated, options);
  }

  // --- Avantis ---

  /**
   * Get Avantis vault deposit and redemption prices.
   * Returns both the gross share value (deposit) and net after fees (redemption).
   */
  async getAvantisPrice(
    chain: ChainSlug | undefined,
    params: AvantisVaultParams,
    options?: RequestOptions,
  ): Promise<AvantisVaultPriceData> {
    const validated = parseParams(getAvantisPriceParamsSchema, {
      chain: this.resolve(chain),
      vault: params.vault,
    });
    return this.client.jsonRpc<AvantisVaultPriceData>('getAvantisPrice', validated, options);
  }

  // --- Pendle ---

  /** Get Pendle V2 info for a chain. */
  async getPendleInfo(chain?: ChainSlug, options?: RequestOptions): Promise<PendleInfoResult> {
    const validated = parseParams(getPendleV2InfoParamsSchema, { chain: this.resolve(chain) });
    return this.client.jsonRpc<PendleInfoResult>('getPendleV2Info', validated, options);
  }

  // --- Cross-protocol ---

  /** Get DeFi summary for an address across all protocols on a chain. */
  async getSummary(
    chain: ChainSlug | undefined,
    address: string,
    options?: RequestOptions,
  ): Promise<DefiSummaryResult> {
    const validated = parseParams(chainAddressParamsSchema, {
      chain: this.resolve(chain),
      address,
    });
    return this.client.jsonRpc<DefiSummaryResult>('getDefiSummary', validated, options);
  }

  /** Get all token approvals for an address. */
  async getApprovals(
    chain: ChainSlug | undefined,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<ApprovalsData> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(chainAddressParamsSchema, {
      chain: this.resolve(chain),
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<ApprovalsData>('getApprovals', validated, requestOptions);
  }

  /** Get unsigned revoke transaction for a token approval. */
  async getRevokeTransaction(
    chain: ChainSlug | undefined,
    address: string,
    params: { token: string; spender: string },
    options?: RequestOptions,
  ): Promise<RevokeTxResult> {
    const validated = parseParams(getApprovalsRevokeParamsSchema, {
      chain: this.resolve(chain),
      address,
      token: params.token,
      spender: params.spender,
    });
    return this.client.jsonRpc<RevokeTxResult>('getApprovalsRevoke', validated, options);
  }

  // --- Perps / Funding rates ---

  /** Get funding rates across all perps protocols. */
  /**
   * Get the combined funding-rate overview across all perps providers.
   *
   * Returns the server's aggregate shape `{ hyperliquid, dydx, timestamp }` —
   * one array per provider, not a flattened union. If you want a single flat
   * list, splat in the caller: `[...res.hyperliquid, ...res.dydx]`.
   */
  async getAllFundingRates(options?: RequestOptions): Promise<FundingRatesData> {
    const validated = parseParams(getFundingRatesParamsSchema, {});
    return this.client.jsonRpc<FundingRatesData>('getFundingRates', validated, options);
  }

  /** Get Hyperliquid funding rate(s). */
  async getHyperliquidFunding(
    params?: { coin?: string },
    options?: RequestOptions,
  ): Promise<HyperliquidFundingData> {
    const validated = parseParams(getHyperliquidFundingParamsSchema, { coin: params?.coin });
    return this.client.jsonRpc<HyperliquidFundingData>('getHLFunding', validated, options);
  }

  /** Get dYdX funding rate(s). */
  async getDydxFunding(
    params?: { ticker?: string },
    options?: RequestOptions,
  ): Promise<DydxFundingData> {
    const validated = parseParams(getDydxFundingParamsSchema, { ticker: params?.ticker });
    return this.client.jsonRpc<DydxFundingData>('getDydxFunding', validated, options);
  }
}
